import React, {
  useState,
  useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DateTime,
} from 'luxon';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import i18n from 'i18next';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  decodeParam,
} from 'web-utils';
import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
} from 'web-selectors';
import {
  Spinner,
  Calendar,
  AddEntity,
  NewWorkoutEvent,
  NewActivityLogger,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

const mapState = ((state) => {
  const {
    activityLoggerModal,
    newWorkoutEventModal,
    calendarSelectedUser,
    scheduleWorkoutModal,
    newWorkoutsEventUserSelected,
  } = getUiState([
    'newWorkoutEventModal',
    'activityLoggerModal',
    'scheduleWorkoutModal',
    'calendarSelectedUser',
    'newWorkoutsEventUserSelected',
  ])(state);

  const profileId = coreSelectors.getProfile('id')(state);
  return {
    events: coreSelectors.getEventsForBigCalendar(calendarSelectedUser)(state),
    clients: coreSelectors.getCalendarClients(state),
    workouts: coreSelectors.getPersonalWorkouts(
      newWorkoutsEventUserSelected || profileId,
    )(state),
    profileId,
    readStatus: getStatus(
      state,
      [
        'workoutEvents.requests.fetchWorkoutEvents.status',
        'personalWorkouts.requests.fetchPersonalWorkouts.status',
      ],
    ),
    newWorkoutEventModal,
    activityLoggerModal,
    scheduleWorkoutModal,
    calendarSelectedUser,
  };
});

const CalendarPage = () => {
  const navigate = useNavigate();
  const [currentRange, setCurrentRange] = useState({
    from: DateTime.utc().startOf('month'),
    to: DateTime.utc().endOf('month'),
  });

  const {
    events,
    clients,
    workouts,
    profileId,
    readStatus,
    activityLoggerModal,
    newWorkoutEventModal,
    scheduleWorkoutModal,
    calendarSelectedUser,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  const fetchEvents = ({ from, to, userId }: any) => {
    dispatch(
      coreActions.requestLoadWorkoutEvents(userId
        ? {
          params: {
            range_from: from,
            range_to: to,
            scope: 'all',
          },
          userId,
        }
        : {
          params: {
            range_from: from,
            range_to: to,
            scope: 'all_with_clients',
          },
        }),
    );
  };

  const fetchWorkouts = (userId: number) => {
    if (userId && userId !== profileId) {
      dispatch(
        coreActions.requestLoadPersonalWorkouts({
          userId,
        }),
      );
    }
  };

  useEffect(() => {
    const { userId } = decodeParam(window.location.search);
    if (userId) {
      dispatch(
        uiActions.setUiState({
          calendarSelectedUser: Number(userId),
          newWorkoutsEventUserSelected: Number(userId),
        }),
      );
    }
    fetchEvents({
      ...currentRange,
      userId,
    });
    dispatch(
      coreActions.requestLoadPersonalWorkouts({
        userId: userId || profileId,
      }),
    );
    dispatch(
      coreActions.requestClientPersonalEntity({
        clientId: userId,
        qs: {
          range_from: currentRange.from,
          range_to: currentRange.to,
          scope: 'all_with_clients',
        },
        resourceType: 'activityLogs',
      }),
    );
  }, []);

  const closeEventModal = () => {
    dispatch(
      uiActions.setUiState({
        newWorkoutEventModal: {
          isOpen: false,
          startDate: null,
        },
      }),
    );
  };

  const closeAddEntityModal = (data?: any) => {
    dispatch(
      uiActions.setUiState({
        newWorkoutEventModal: {
          isOpen: false,
          startDate: null,
        },
        scheduleWorkoutModal: false,
        ...((data.id && newWorkoutEventModal.payload)
          ? {
            workoutShouldBeSchedule: newWorkoutEventModal.payload,
          }
          : {
            workoutShouldBeSchedule: null,
          }
        ),
      }),
    );

    if (data.id) {
      navigate(`/personal_workouts/${data.id}/edit?schedule=1`);
    }
  };

  const handleCreateWorkout = (values: any) => {
    dispatch(
      uiActions.setUiState({
        newWorkoutEventModal: {
          ...newWorkoutEventModal,
          isOpen: false,
          userId: values.client,
          payload: {
            begins_at: DateTime.fromISO(values.start),
            ends_at: DateTime.fromISO(values.end),
          },
        },
        scheduleWorkoutModal: true,
      }),
    );
  };

  const handleNewEvent = (values: any, actions: any) => {
    const action = values.originalId
      ? coreActions.requestUpdateWorkoutEvent
      : coreActions.requestCreateWorkoutEvents;

    dispatch(
      action({
        id: values.originalId,
        userId: values.client,
        payload: {
          begins_at: DateTime.fromISO(values.start),
          ends_at: DateTime.fromISO(values.end),
          person_id: Number(values.client),
          personal_workout_id: values.workout,
        },
        callbacks: {
          onError: closeEventModal,
          onSuccess: closeEventModal,
          ...actions,
        },
      }),
    );
  };

  const handleAddEntity = (values: any, actions: any) => {
    if (newWorkoutEventModal.payload) {
      dispatch(
        coreActions.requestCreatePersonalEntity({
          clientId: newWorkoutEventModal.userId,
          payload: {
            ...values,
            is_cooldown: false,
            is_visible: false,
            is_warmup: false,
          },
          callbacks: {
            ...actions,
            onError: closeAddEntityModal,
            onSuccess: closeAddEntityModal,
          },
          resourceType: 'personalWorkouts',
        }),
      );
    } else {
      closeAddEntityModal();
    }
  };

  return (
    <Flex fillSpace>
      <Calendar
        onFilter={(userId) => {
          dispatch(
            uiActions.setUiState({
              calendarSelectedUser: userId,
              newWorkoutsEventUserSelected: userId,
            }),
          );
          fetchEvents({
            userId,
            ...currentRange,
          });
          fetchWorkouts(userId);
        }}
        events={events}
        clients={clients}
        calendarSelectedUser={calendarSelectedUser}
        onNewWorkoutClick={
          ({ start }) => {
            dispatch(
              uiActions.setUiState({
                newWorkoutEventModal: {
                  isOpen: true,
                  initialValues: {
                    client: calendarSelectedUser || profileId,
                    start: DateTime.fromJSDate(start),
                    end: DateTime.fromJSDate(start).plus({ hours: 1 }),
                  },
                },
              }),
            );
          }
        }
        onNavigate={(day) => {
          const from = DateTime.fromJSDate(day).startOf('month');
          const to = DateTime.fromJSDate(day).endOf('month');
          setCurrentRange({
            from,
            to,
          });
          fetchEvents({
            from,
            to,
            userId: calendarSelectedUser,
          });
        }}
      />

      {readStatus.pending && (
        <Spinner over />
      )}

      <NewWorkoutEvent
        isOpen={newWorkoutEventModal.isOpen}
        clients={clients}
        workouts={workouts}
        initialValues={newWorkoutEventModal.initialValues || {}}
        onSubmit={handleNewEvent}
        onUserSelected={(userId) => {
          dispatch(
            uiActions.setUiState({
              newWorkoutsEventUserSelected: userId,
            }),
          );
          fetchWorkouts(userId);
        }}
        onLogActivity={(startDate) => {
          closeEventModal();
          dispatch(
            uiActions.setUiState({
              activityLoggerModal: {
                isOpen: true,
                startDate: new Date(startDate.toISO()),
              },
            }),
          );
        }}
        onRequestClose={closeEventModal}
        onRequestCreateWorkout={handleCreateWorkout}
      />

      <NewActivityLogger
        isOpen={activityLoggerModal.isOpen}
        clients={clients}
        startDate={activityLoggerModal.startDate}
        initialClient={calendarSelectedUser || profileId}
        initialValues={activityLoggerModal.initialValues}
        onSubmit={(payload, actions) => {
          const editId = activityLoggerModal.initialValues?.id;
          const action = editId
            ? coreActions.requestUpdateEntity
            : coreActions.requestAddEntity;

          dispatch(
            action({
              id: editId,
              payload,
              resourceType: 'activityLogs',
              callbacks: {
                ...actions,
                onSuccess: () => {
                  dispatch(
                    uiActions.setUiState({
                      activityLoggerModal: {
                        isOpen: false,
                        startDate: null,
                      },
                    }),
                  );
                },
              },
            }),
          );
        }}
        onRequestClose={() => {
          dispatch(
            uiActions.setUiState({
              activityLoggerModal: {
                isOpen: false,
                startDate: null,
              },
            }),
          );
        }}
      />

      <AddEntity
        label={i18n.t('schedule.new.workout')}
        closeButton
        buttonLabel="Schedule"
        appearance="danger"
        isOpen={scheduleWorkoutModal}
        onSubmit={handleAddEntity}
        onRequestClose={closeAddEntityModal}
      />
    </Flex>
  );
};

export default CalendarPage;
